/**
 * Step 2: 데이터 소스 설정
 *
 * 선택된 소스(file / web / database)별 설정 컴포넌트를 탭으로 조합합니다.
 * - 단일 소스: 탭 없이 해당 소스 설정만 표시
 * - 복합 소스: SourceTabs로 탭 전환하여 각 소스 설정 표시
 *
 * 각 소스 컴포넌트(FileSourceStep, WebSourceStep, DatabaseSourceStep)를
 * 그대로 재사용하므로 단일 소스에서 수정된 내용이 복합 소스에도 자동 반영됩니다.
 */

import { SourceType, WebConfig, DbConfig } from '../shared/pipeline.types';
import { SourceTabs } from '../shared/SourceTabs';
import { FileSourceStep, DriveFileSelection, DropboxFileSelection } from './FileSourceStep';
import { WebSourceStep } from './WebSourceStep';
import { DatabaseSourceStep } from './DatabaseSourceStep';

interface DataSourceStepProps {
  // 어떤 소스가 활성화되어 있는지
  activeSources: SourceType[];
  activeTab: SourceType;
  onActiveTabChange: (tab: SourceType) => void;

  // 파일 소스
  hasFile: boolean;
  files: File[];
  onFilesChange: (files: File[]) => void;
  isDragOver: boolean;
  onIsDragOverChange: (v: boolean) => void;
  driveFiles?: DriveFileSelection[];
  onDriveFilesChange?: (files: DriveFileSelection[]) => void;
  dropboxFiles?: DropboxFileSelection[];
  onDropboxFilesChange?: (files: DropboxFileSelection[]) => void;

  // 웹 소스
  hasWeb: boolean;
  webConfig: WebConfig;
  onWebConfigChange: (config: WebConfig) => void;

  // DB 소스
  hasDatabase: boolean;
  dbConfig: DbConfig;
  onDbConfigChange: (config: DbConfig) => void;
}

export function DataSourceStep({
  activeSources,
  activeTab,
  onActiveTabChange,
  hasFile,
  files,
  onFilesChange,
  isDragOver,
  onIsDragOverChange,
  driveFiles,
  onDriveFilesChange,
  dropboxFiles,
  onDropboxFilesChange,
  hasWeb,
  webConfig,
  onWebConfigChange,
  hasDatabase,
  dbConfig,
  onDbConfigChange,
}: DataSourceStepProps) {
  return (
    <div className="form-item">
      {/* 복합 소스 탭 — 단일 소스면 SourceTabs 내부에서 null 반환 */}
      <SourceTabs
        sources={activeSources}
        active={activeTab}
        onChange={onActiveTabChange}
      />

      {/* 파일 소스 설정 */}
      {activeTab === 'file' && hasFile && (
        <FileSourceStep
          files={files}
          onFilesChange={onFilesChange}
          isDragOver={isDragOver}
          onIsDragOverChange={onIsDragOverChange}
          driveFiles={driveFiles}
          onDriveFilesChange={onDriveFilesChange}
          dropboxFiles={dropboxFiles}
          onDropboxFilesChange={onDropboxFilesChange}
        />
      )}

      {/* 웹 소스 설정 */}
      {activeTab === 'web' && hasWeb && (
        <WebSourceStep
          config={webConfig}
          onChange={onWebConfigChange}
        />
      )}

      {/* DB 소스 설정 */}
      {activeTab === 'database' && hasDatabase && (
        <DatabaseSourceStep
          config={dbConfig}
          onChange={onDbConfigChange}
        />
      )}
    </div>
  );
}
